// fetchUploadImage.js - API functions for image upload management for payments
import { enhancedTokenManager } from '../utils/enhancedTokenManager';

const API_URL = process.env.REACT_APP_API_URL || 'https://localhost:7204';

// Helper function to get auth headers for JSON requests
const getAuthHeaders = () => {
  const token = enhancedTokenManager.getToken();
  
  if (!token) {
    throw new Error('No authentication token found');
  }

  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
};

// Helper function to get auth headers for FormData requests (without Content-Type)
const getAuthHeadersForFormData = () => {
  const token = enhancedTokenManager.getToken();
  
  if (!token) {
    throw new Error('No authentication token found');
  }

  return {
    'Authorization': `Bearer ${token}`
  };
};

// ============ EMAIL CONTENT SAVING FUNCTIONS ============

// Save email content as HTML file
export const saveEmailContentAsFile = async (emailContent, buildingId, paymentId = null, referenceNumber = null, description = '') => {
  try {
    const response = await fetch(`${API_URL}/api/UploadImage/SaveEmailContent`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        EmailContent: emailContent,
        BuildingId: buildingId,
        SubFolder: 'receipts',
        PaymentId: paymentId,
        ReferenceNumber: referenceNumber,
        Description: description
      })
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to save email content');
    }

    return data;
  } catch (error) {
    throw error;
  }
};

// Get email content file
export const getEmailContentFile = async (filePath) => {
  try {
    const token = enhancedTokenManager.getToken();
    
    if (!token) {
      throw new Error('No authentication token found');
    }

    const response = await fetch(`${API_URL}/api/UploadImage/GetImage?filePath=${encodeURIComponent(filePath)}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to get email content file');
    }

    return response;
  } catch (error) {
    throw error;
  }
};

// View email content file in new tab
export const viewEmailContentInNewTab = async (filePath) => {
  try {
    const token = enhancedTokenManager.getToken();
    const url = `${API_URL}/api/UploadImage/ViewImage?filePath=${encodeURIComponent(filePath)}&token=${token}`;
    window.open(url, '_blank');
    return { success: true, message: 'Email content opened in new tab' };
  } catch (error) {
    throw error;
  }
};

// Download email content file
export const downloadEmailContentFile = async (filePath, fileName = null) => {
  try {
    const response = await getEmailContentFile(filePath);
    const blob = await response.blob();
    
    // Create download link
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName || filePath.split(/[/\\]/).pop() || 'email_content.html';
    document.body.appendChild(link);
    link.click();
    
    // Cleanup
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    return { success: true, message: 'Download started' };
  } catch (error) {
    throw error;
  }
};

// ============ PROFILE PICTURE SPECIFIC FUNCTIONS ============

// Upload profile picture with userId
export const uploadProfilePicture = async (file, buildingId, userId, description = '') => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('buildingId', buildingId.toString());
    formData.append('userId', userId.toString());
    formData.append('description', description);
    formData.append('subFolder', 'profiles');

    const response = await fetch(`${API_URL}/api/UploadImage/AddImage`, {
      method: 'POST',
      headers: getAuthHeadersForFormData(),
      body: formData,
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Profile picture upload failed');
    }

    return data;
  } catch (error) {
    throw error;
  }
};

// Delete profile picture with userId
export const deleteProfilePicture = async (filePath, userId) => {
  try {
    const response = await fetch(`${API_URL}/api/UploadImage/DeleteImage`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        FilePath: filePath,
        UserId: userId
      })
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Profile picture delete failed');
    }

    return data;
  } catch (error) {
    throw error;
  }
};

// Get profile picture as blob URL for display
export const getProfilePictureAsBlob = async (filePath) => {
  try {
    const response = await viewImage(filePath);
    const blob = await response.blob();
    return URL.createObjectURL(blob);
  } catch (error) {
    throw error;
  }
};

// ============ EXISTING FUNCTIONS ============

// Upload new image/file for payments
export const uploadImage = async (file, buildingId, description = '', subFolder = 'general') => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('buildingId', buildingId.toString());
    formData.append('description', description);
    formData.append('subFolder', subFolder);

    const response = await fetch(`${API_URL}/api/UploadImage/AddImage`, {
      method: 'POST',
      headers: getAuthHeadersForFormData(),
      body: formData,
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Upload failed');
    }

    return data;
  } catch (error) {
    throw error;
  }
};

// Upload payment receipt
export const uploadPaymentReceipt = async (file, buildingId, description = '') => {
  return uploadImage(file, buildingId, description, 'receipts');
};

// Upload payment document
export const uploadPaymentDocument = async (file, buildingId, description = '') => {
  return uploadImage(file, buildingId, description, 'documents');
};

// Upload general payment file
export const uploadGeneralPaymentFile = async (file, buildingId, description = '') => {
  return uploadImage(file, buildingId, description, 'general');
};

// Delete image - Updated to support userId for profile pictures
export const deleteImage = async (filePath, buildingId = null, userId = null) => {
  try {
    const requestBody = {
      FilePath: filePath
    };

    // Add buildingId if provided
    if (buildingId) {
      requestBody.buildingId = buildingId;
    }

    // Add userId if provided (for profile pictures)
    if (userId) {
      requestBody.UserId = userId;
    }

    const response = await fetch(`${API_URL}/api/UploadImage/DeleteImage`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(requestBody)
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Delete failed');
    }

    return data;
  } catch (error) {
    throw error;
  }
};

// Get image file for download
export const getImage = async (filePath) => {
  try {
    const token = enhancedTokenManager.getToken();
    
    if (!token) {
      throw new Error('No authentication token found');
    }

    const response = await fetch(`${API_URL}/api/UploadImage/GetImage?filePath=${encodeURIComponent(filePath)}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to get image');
    }

    return response;
  } catch (error) {
    throw error;
  }
};

// View image file (inline viewing) - Use this for displaying profile pictures
export const viewImage = async (filePath) => {
  try {
    const token = enhancedTokenManager.getToken();
    
    if (!token) {
      throw new Error('No authentication token found');
    }

    const response = await fetch(`${API_URL}/api/UploadImage/ViewImage?filePath=${encodeURIComponent(filePath)}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to view image: ${response.status} ${response.statusText}`);
    }

    return response;
  } catch (error) {
    throw error;
  }
};

// Get image as blob URL for display
export const getImageAsBlob = async (filePath) => {
  try {
    const response = await viewImage(filePath);
    const blob = await response.blob();
    return URL.createObjectURL(blob);
  } catch (error) {
    throw error;
  }
};

// Create folder
export const createFolder = async (customFolderName = null, targetDate = null, subFolder = 'general') => {
  try {
    const requestBody = {
      subFolder: subFolder
    };
    
    if (customFolderName) {
      requestBody.customFolderName = customFolderName;
    }
    
    if (targetDate) {
      requestBody.targetDate = targetDate;
    }

    const response = await fetch(`${API_URL}/api/UploadImage/CreateFolderImage`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(requestBody)
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Create folder failed');
    }

    return data;
  } catch (error) {
    throw error;
  }
};

// Get folder contents
export const getFolderContents = async (folderName = null, subFolder = 'general') => {
  try {
    const token = enhancedTokenManager.getToken();
    
    if (!token) {
      throw new Error('No authentication token found');
    }

    let url = `${API_URL}/api/UploadImage/GetFolderContents?subFolder=${encodeURIComponent(subFolder)}`;
    
    if (folderName) {
      url += `&folderName=${encodeURIComponent(folderName)}`;
    }

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Get folder contents failed');
    }

    return data;
  } catch (error) {
    throw error;
  }
};

// Get all folders
export const getAllFolders = async (subFolder = 'general') => {
  try {
    const token = enhancedTokenManager.getToken();
    
    if (!token) {
      throw new Error('No authentication token found');
    }

    const response = await fetch(`${API_URL}/api/UploadImage/GetAllFolders?subFolder=${encodeURIComponent(subFolder)}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Get all folders failed');
    }

    return data;
  } catch (error) {
    throw error;
  }
};

// Generate monthly folder name (utility function) - Updated format
export const generateMonthlyFolderName = (date = new Date()) => {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${month}${year}`; // Format: 012025, 022025, etc.
};

// Check if folder exists for current month
export const checkCurrentMonthFolder = async (subFolder = 'general') => {
  try {
    const currentFolderName = generateMonthlyFolderName();
    const folderData = await getFolderContents(currentFolderName, subFolder);
    return {
      exists: true,
      folderName: currentFolderName,
      subFolder: subFolder,
      ...folderData
    };
  } catch (error) {
    if (error.message.includes('Folder not found')) {
      return {
        exists: false,
        folderName: generateMonthlyFolderName(),
        subFolder: subFolder
      };
    }
    throw error;
  }
};

// Create current month folder if it doesn't exist
export const ensureCurrentMonthFolder = async (subFolder = 'general') => {
  try {
    const currentCheck = await checkCurrentMonthFolder(subFolder);
    
    if (!currentCheck.exists) {
      const result = await createFolder(null, new Date(), subFolder);
      return {
        created: true,
        ...result
      };
    }
    
    return {
      created: false,
      exists: true,
      folderName: currentCheck.folderName,
      subFolder: subFolder
    };
  } catch (error) {
    // Try to create folder anyway
    try {
      const result = await createFolder(null, new Date(), subFolder);
      return {
        created: true,
        fallback: true,
        ...result
      };
    } catch (createError) {
      throw createError;
    }
  }
};

// Upload image with automatic folder creation
export const uploadImageWithFolderCheck = async (file, buildingId, description = '', subFolder = 'general') => {
  try {
    // Ensure current month folder exists
    await ensureCurrentMonthFolder(subFolder);
    
    // Upload the image
    const uploadResult = await uploadImage(file, buildingId, description, subFolder);
    
    return uploadResult;
  } catch (error) {
    throw error;
  }
};

// Upload payment receipt with folder check
export const uploadPaymentReceiptWithFolderCheck = async (file, buildingId, description = '') => {
  return uploadImageWithFolderCheck(file, buildingId, description, 'receipts');
};

// Download image file
export const downloadImage = async (filePath, fileName = null) => {
  try {
    const response = await getImage(filePath);
    const blob = await response.blob();
    
    // Create download link
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName || filePath.split(/[/\\]/).pop() || 'download';
    document.body.appendChild(link);
    link.click();
    
    // Cleanup
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    return { success: true, message: 'Download started' };
  } catch (error) {
    throw error;
  }
};

// View image in new tab
export const viewImageInNewTab = async (filePath) => {
  try {
    const token = enhancedTokenManager.getToken();
    const url = `${API_URL}/api/UploadImage/ViewImage?filePath=${encodeURIComponent(filePath)}&token=${token}`;
    window.open(url, '_blank');
    return { success: true, message: 'Image opened in new tab' };
  } catch (error) {
    throw error;
  }
};

// ============ PAYMENT RECEIPT API FUNCTIONS ============

// Generate receipt for payment
export const generatePaymentReceipt = async (paymentId, buildingContactEmail = '', companyName = 'ועד הבית', buildingAddress = '') => {
  try {
    const token = enhancedTokenManager.getToken();
    const response = await fetch(`${API_URL}/api/PaymentReceipt/GenerateReceipt`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        PaymentId: paymentId,
        BuildingContactEmail: buildingContactEmail,
        CompanyName: companyName,
        BuildingAddress: buildingAddress
      })
    });

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || 'Failed to generate receipt');
    }

    return data;
  } catch (error) {
    throw error;
  }
};

// Fetch payment receipts for building
export const fetchPaymentReceipts = async (buildingId) => {
  try {
    const token = enhancedTokenManager.getToken();
    const response = await fetch(`${API_URL}/api/PaymentReceipt/GetPaymentReceipts/${buildingId}`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || 'Failed to fetch receipts');
    }

    return data;
  } catch (error) {
    throw error;
  }
};

// Download payment receipt
export const downloadPaymentReceipt = async (paymentId) => {
  try {
    const token = enhancedTokenManager.getToken();
    const response = await fetch(`${API_URL}/api/PaymentReceipt/DownloadReceipt/${paymentId}`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (response.ok) {
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `receipt_${paymentId}.html`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      return { success: true, message: 'Receipt downloaded successfully' };
    } else {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to download receipt');
    }
  } catch (error) {
    throw error;
  }
};

// View payment receipt in new tab
export const viewPaymentReceiptInNewTab = async (paymentId) => {
  try {
    const token = enhancedTokenManager.getToken();
    const url = `${API_URL}/api/PaymentReceipt/ViewReceipt/${paymentId}?token=${token}`;
    window.open(url, '_blank');
    return { success: true, message: 'Receipt opened in new tab' };
  } catch (error) {
    throw error;
  }
};

// Get receipts folders specifically
export const getReceiptsFolders = async () => {
  return getAllFolders('receipts');
};

// Get documents folders specifically  
export const getDocumentsFolders = async () => {
  return getAllFolders('documents');
};

// ============ UTILITY FUNCTIONS ============

// Validate file before upload
export const validateFile = (file) => {
  const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'application/pdf'];
  const maxSize = 10 * 1024 * 1024; // 10MB

  if (!file) {
    return { valid: false, error: 'No file selected' };
  }

  if (!allowedTypes.includes(file.type)) {
    return { valid: false, error: 'Invalid file type. Only images and PDFs are allowed.' };
  }

  if (file.size > maxSize) {
    return { valid: false, error: 'File size exceeds 10MB limit.' };
  }

  return { valid: true };
};

// Format file size for display
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Get file extension
export const getFileExtension = (filename) => {
  return filename.split('.').pop()?.toLowerCase() || '';
};

// Check if file is image
export const isImageFile = (filename) => {
  const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'];
  return imageExtensions.includes(getFileExtension(filename));
};

// Check if file is PDF
export const isPdfFile = (filename) => {
  return getFileExtension(filename) === 'pdf';
};

// Check if file is HTML
export const isHtmlFile = (filename) => {
  return getFileExtension(filename) === 'html';
};

// Get appropriate icon for file type
export const getFileTypeIcon = (filename) => {
  if (isImageFile(filename)) return '🖼️';
  if (isPdfFile(filename)) return '📄';
  if (isHtmlFile(filename)) return '📧';
  return '📎';
};

// Format date for folder names (MM/YYYY format)
export const formatDateForFolder = (date = new Date()) => {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${month}/${year}`;
};

// Parse folder name to date
export const parseFolderNameToDate = (folderName) => {
  // folderName format: 012025 (MMYYYY)
  if (folderName.length !== 6) return null;
  
  const month = parseInt(folderName.substring(0, 2));
  const year = parseInt(folderName.substring(2, 6));
  
  if (month < 1 || month > 12 || year < 2020 || year > 2030) return null;
  
  return new Date(year, month - 1, 1);
};

// Get Hebrew month name
export const getHebrewMonthName = (monthNumber) => {
  const hebrewMonths = {
    1: 'ינואר', 2: 'פברואר', 3: 'מרץ', 4: 'אפריל',
    5: 'מאי', 6: 'יוני', 7: 'יולי', 8: 'אוגוסט',
    9: 'ספטמבר', 10: 'אוקטובר', 11: 'נובמבר', 12: 'דצמבר'
  };
  return hebrewMonths[monthNumber] || 'לא ידוע';
};

// Format folder name for display
export const formatFolderNameForDisplay = (folderName) => {
  const date = parseFolderNameToDate(folderName);
  if (!date) return folderName;
  
  const month = date.getMonth() + 1;
  const year = date.getFullYear();
  const hebrewMonth = getHebrewMonthName(month);
  
  return `${hebrewMonth} ${year}`;
};

export default {
  // Image upload functions
  uploadImage,
  uploadPaymentReceipt,
  uploadPaymentDocument,
  uploadGeneralPaymentFile,
  deleteImage,
  getImage,
  viewImage,
  getImageAsBlob,
  downloadImage,
  viewImageInNewTab,
  
  // Profile picture functions
  uploadProfilePicture,
  deleteProfilePicture,
  getProfilePictureAsBlob,
  
  // Email content functions
  saveEmailContentAsFile,
  getEmailContentFile,
  viewEmailContentInNewTab,
  downloadEmailContentFile,
  
  // Folder management
  createFolder,
  getFolderContents,
  getAllFolders,
  getReceiptsFolders,
  getDocumentsFolders,
  generateMonthlyFolderName,
  checkCurrentMonthFolder,
  ensureCurrentMonthFolder,
  uploadImageWithFolderCheck,
  uploadPaymentReceiptWithFolderCheck,
  
  // Payment receipt functions
  generatePaymentReceipt,
  fetchPaymentReceipts,
  downloadPaymentReceipt,
  viewPaymentReceiptInNewTab,
  
  // Utility functions
  validateFile,
  formatFileSize,
  getFileExtension,
  isImageFile,
  isPdfFile,
  isHtmlFile,
  getFileTypeIcon,
  formatDateForFolder,
  parseFolderNameToDate,
  getHebrewMonthName,
  formatFolderNameForDisplay
};